// D9-11 (#408) — the HIN-showing, inventory-marking boat picker for the D9-03
// work-order Sheet. Three obligations live here: every option shows the HIN
// (or says plainly that none is recorded) so the advisor never guesses which
// hull they're booking; a boat sourced from inventory carries a visible
// "From inventory" mark; and "No boat on file" is a first-class, explicit
// choice rather than a coerced empty value. The empty state (a customer with
// zero boats on file) offers the D9-12 Add boat path instead of a dead end.
//
// 09-UAT gap 1 — this component does NOT mount `CustomerBoatQuickCreateDialog`
// itself. It only REQUESTS the dialog via `onAddBoat`; the caller owns the
// mount, placed as a sibling of the caller's own `<form>`, never inside it. A
// dialog rendered inside a `<form>` submits that form on its own submit —
// React's synthetic submit event bubbles up through a Radix portal even
// though the portal's DOM node does not live inside the form element.
import { Plus } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@marine/ui/select';
import { Badge } from '@marine/ui/badge';
import { useCustomerBoats } from '@/modules/crm/hooks/useCustomers';
import type { CustomerBoat } from '@/modules/crm/types/crm.types';

const ADD_BOAT_VALUE = '__add_boat__';
const NO_BOAT_VALUE = '__no_boat__';

interface BoatSelectProps {
  customerId: string | null;
  value: string | null;
  onChange: (boatId: string | null) => void;
  onAddBoat: () => void;
  disabled?: boolean;
}

function boatLabel(boat: CustomerBoat): string {
  return [boat.year, boat.manufacturer, boat.model].filter(Boolean).join(' ') || boat.name || 'Boat';
}

export function BoatSelect({ customerId, value, onChange, onAddBoat, disabled }: BoatSelectProps) {
  const { data } = useCustomerBoats(customerId ?? '');
  const boats = customerId ? (data?.items ?? []) : [];
  const isDisabled = !!disabled || !customerId;

  const selectValue = !customerId ? undefined : value === null ? NO_BOAT_VALUE : (value ?? undefined);

  function handleValueChange(next: string): void {
    if (!customerId) return;

    // 09-HUMAN-UAT item 5 (2026-09-09) — '' is never a value this picker
    // offers (options are ADD_BOAT_VALUE, NO_BOAT_VALUE or a boat ULID); it is
    // Radix Select's own echo. Because this picker sits inside the Sheet's
    // <form>, Radix renders a hidden native <select> (SelectBubbleInput) and,
    // on every controlled-value change, assigns that value to the native
    // element and re-dispatches `change`. When the value is a boat whose
    // <option> has not rendered yet (the Add-boat hand-off sets `boat_id`
    // before the boats refetch lands), the browser coerces the unmatched
    // native value to '' and Radix calls onValueChange(''). Swallowing it
    // keeps the caller's `boat_id`; the label appears as soon as the option
    // mounts. Without this the Sheet showed the placeholder after
    // "Boat added" and sent boat_id = ''.
    if (next === '') return;

    if (next === ADD_BOAT_VALUE) {
      onAddBoat();
      return;
    }

    onChange(next === NO_BOAT_VALUE ? null : next);
  }

  return (
    <>
      <Select value={selectValue} onValueChange={handleValueChange} disabled={isDisabled}>
        <SelectTrigger aria-label="Boat">
          <SelectValue placeholder={customerId ? 'Select a boat' : 'Choose a customer first'} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ADD_BOAT_VALUE}>
            <span className="flex items-center gap-1.5 text-link">
              <Plus className="h-3.5 w-3.5" aria-hidden="true" />
              Add boat
            </span>
          </SelectItem>
          <SelectItem value={NO_BOAT_VALUE}>No boat on file</SelectItem>
          {boats.map((boat) => (
            <SelectItem key={boat.id} value={boat.id}>
              <span className="flex flex-col items-start gap-0.5">
                <span className="flex items-center gap-1.5">
                  <span>{boatLabel(boat)}</span>
                  {boat.inventory_boat_id && (
                    <Badge variant="info" appearance="light" size="sm">
                      From inventory
                    </Badge>
                  )}
                </span>
                <span className="text-xs text-muted-foreground">
                  {boat.hin ? `HIN: ${boat.hin}` : 'HIN not recorded'}
                </span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* D9-11 obligation 4 — the empty state is an invitation, not a dead end. */}
      {customerId && boats.length === 0 && (
        <p className="text-xs text-muted-foreground">No boat on file — add one below</p>
      )}
    </>
  );
}
